import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useInventory } from '../../contexts/InventoryContext';
import { useNotification } from '../../contexts/NotificationContext';
import { ensureDefaultLocations, fetchLocationQuantitiesForProducts, LocationQuantity } from '../../utils/locationInventory';
import { getAdminId } from '../../utils/getAdminId';
import { useDamageItems, ReportDamageInput } from '../../hooks/useDamageItems';
import { useProductAttributes } from '../../hooks/use-product-attributes';
import { ItemPicker } from '../inventory/ItemPicker';
import { QuantityStepper } from '../ui/QuantityStepper';
import { ItemImageUpload } from '../ui/ItemImageUpload';
import { Button } from '../ui/Button';
import {
  Package,
  MapPin,
  Save,
  AlertTriangle,
  FileText,
  Loader2,
  RotateCcw,
} from 'lucide-react';

export const DamageReportForm: React.FC = () => {
  const { user } = useAuth();
  const { allItems, allItemsLoading, fetchAllItems, invalidateAllItemsCache } = useInventory();
  const { success, error: showError } = useNotification();

  const adminId = getAdminId(user);
  const { reportDamage } = useDamageItems(adminId);
  const { colors, sizes } = useProductAttributes(adminId);

  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [productLocationQtys, setProductLocationQtys] = useState<Record<string, LocationQuantity[]>>({});

  useEffect(() => {
    if (adminId) {
      fetchAllItems();
      ensureDefaultLocations(adminId).then(setLocations);
    }
  }, [adminId, fetchAllItems]);

  useEffect(() => {
    if (adminId && allItems.length > 0) {
      loadProductLocationQtys();
    }
  }, [adminId, allItems.length]);

  const loadProductLocationQtys = useCallback(async () => {
    if (!adminId || allItems.length === 0) return;
    const productIds = allItems.map(item => item.id);
    const qtysMap = await fetchLocationQuantitiesForProducts(productIds, adminId);
    const result: Record<string, LocationQuantity[]> = {};
    qtysMap.forEach((qtys, productId) => {
      result[productId] = qtys;
    });
    setProductLocationQtys(result);
  }, [adminId, allItems]);

  const selectedProductData = useMemo(
    () => allItems.find(p => p.id === selectedProduct),
    [allItems, selectedProduct]
  );

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort();
  }, [allItems]);

  const getLocationQty = useCallback((productId: string, locationId: string): number => {
    const qtys = productLocationQtys[productId];
    if (!qtys) return 0;
    const loc = qtys.find(q => q.locationId === locationId);
    return loc?.quantity ?? 0;
  }, [productLocationQtys]);

  const locationQtyForSelected = selectedProduct && selectedLocationId
    ? getLocationQty(selectedProduct, selectedLocationId)
    : 0;

  const handleProductSelect = (item: any) => {
    setSelectedProduct(item.id);
    setQuantity(1);
    setSelectedLocationId('');
  };

  const handleProductClear = () => {
    setSelectedProduct('');
    setQuantity(1);
    setSelectedLocationId('');
  };

  const handleReset = () => {
    handleProductClear();
    setDescription('');
    setImageUrl(undefined);
  };

  const canSave = selectedProduct
    && selectedLocationId
    && quantity > 0
    && description.trim()
    && quantity <= locationQtyForSelected;

  const handleSave = async () => {
    if (!canSave || !adminId || !user) return;

    const locationData = locations.find(l => l.id === selectedLocationId);
    if (!locationData || !selectedProductData) return;

    setSaving(true);
    try {
      const input: ReportDamageInput = {
        adminId,
        userId: user.id,
        userName: user.full_name || user.email || 'Unknown',
        productId: selectedProduct,
        productName: selectedProductData.name,
        quantity,
        colorId: selectedProductData.color_id || undefined,
        sizeId: selectedProductData.size_id || undefined,
        locationId: selectedLocationId,
        locationName: locationData.name,
        description: description.trim(),
        imageUrl,
      };

      const ok = await reportDamage(input);
      if (ok) {
        success(`Reported ${quantity}x "${selectedProductData.name}" as damaged`);
        handleReset();
        invalidateAllItemsCache();
        await fetchAllItems(true);
        await loadProductLocationQtys();
      } else {
        showError('Failed to report damage. Please try again.');
      }
    } catch (err) {
      console.error('Error saving damage report:', err);
      showError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '';
  const getColorHex = (colorId?: string) => colors.find(c => c.id === colorId)?.hex_code || '';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-xl">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Report Damaged Item</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Record damaged inventory and deduct from the selected location
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Product
              </label>
              <ItemPicker
                items={allItems}
                colors={colors}
                sizes={sizes}
                allCategories={allCategories}
                selectedItemId={selectedProduct || null}
                onSelect={handleProductSelect}
                onClear={handleProductClear}
              />
            </div>

            {selectedProductData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Location
                  </label>
                  <select
                    value={selectedLocationId}
                    onChange={(e) => {
                      setSelectedLocationId(e.target.value);
                      setQuantity(1);
                    }}
                    className="w-full h-11 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
                  >
                    <option value="">Select location...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({getLocationQty(selectedProduct, loc.id)} available)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Damaged Quantity
                  </label>
                  <QuantityStepper
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                    max={locationQtyForSelected || 1}
                    disabled={!selectedLocationId}
                  />
                  {selectedLocationId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {locationQtyForSelected} available at this location
                    </p>
                  )}
                  {quantity > locationQtyForSelected && selectedLocationId && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Exceeds available quantity
                    </p>
                  )}
                </div>
              </div>
            )}

            {selectedProductData && (getColorName(selectedProductData.color_id) || getSizeName(selectedProductData.size_id)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Variant
                </label>
                <div className="flex flex-wrap gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                  {getColorName(selectedProductData.color_id) && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                      {getColorHex(selectedProductData.color_id) && (
                        <span
                          className="w-3 h-3 rounded-full border border-amber-300 dark:border-amber-600"
                          style={{ backgroundColor: getColorHex(selectedProductData.color_id) }}
                        />
                      )}
                      {getColorName(selectedProductData.color_id)}
                    </span>
                  )}
                  {getSizeName(selectedProductData.size_id) && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      {getSizeName(selectedProductData.size_id)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From selected product</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                Damage Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe how the item is damaged..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all resize-none"
              />
            </div>
          </div>

          <div className="space-y-5">
            {selectedProductData && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                      {selectedProductData.name}
                    </div>
                    {selectedProductData.reference_code && (
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium truncate">
                        {selectedProductData.reference_code}
                      </div>
                    )}
                  </div>
                </div>

                {(getColorName(selectedProductData.color_id) || getSizeName(selectedProductData.size_id)) && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {getColorName(selectedProductData.color_id) && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                        {getColorHex(selectedProductData.color_id) && (
                          <span
                            className="w-2.5 h-2.5 rounded-full border border-amber-300 dark:border-amber-600"
                            style={{ backgroundColor: getColorHex(selectedProductData.color_id) }}
                          />
                        )}
                        {getColorName(selectedProductData.color_id)}
                      </span>
                    )}
                    {getSizeName(selectedProductData.size_id) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {getSizeName(selectedProductData.size_id)}
                      </span>
                    )}
                  </div>
                )}

                {selectedLocationId && (
                  <div className="flex items-center justify-between text-sm p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-red-100 dark:border-red-800">
                    <span className="text-gray-600 dark:text-gray-400">After damage:</span>
                    <span className="font-bold text-red-600 dark:text-red-400">
                      {locationQtyForSelected - quantity} (-{quantity})
                    </span>
                  </div>
                )}
              </div>
            )}

            <ItemImageUpload
              value={imageUrl}
              onChange={setImageUrl}
              adminId={adminId || ''}
              disabled={saving}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Report Damage'}
          </Button>
        </div>
      </div>
    </div>
  );
};
